import React, { useEffect, useState, useRef } from 'react';
import { getHeroText } from '../services/heroTextService';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';

const HERO_TEXT_COLLECTION = 'heroTextSettings';
const HERO_TEXT_DOC_ID = 'main';

function HeroText({ shouldAnimate = false }) {
  const [isVisible, setIsVisible] = useState(false);
  const [textColorMode, setTextColorMode] = useState('gradient'); // 'gradient', 'light', 'dark'
  const [heroText, setHeroText] = useState({
    titleLine1: 'Where',
    titleHighlight: 'TIME',
    titleLine2: 'pays',
    description: "We don't wait for success, we accelerate it."
  });
  const timeTextRef = useRef(null);
  const heroSectionRef = useRef(null);

  // Load hero text from Firestore
  useEffect(() => {
    const loadHeroText = async () => {
      try {
        const data = await getHeroText();
        setHeroText({
          titleLine1: data.titleLine1 || 'Where',
          titleHighlight: data.titleHighlight || 'TIME',
          titleLine2: data.titleLine2 || 'pays',
          description: data.description || "We don't wait for success, we accelerate it."
        });
      } catch (error) {
        console.error('Error loading hero text:', error);
        // Keep default values on error
      }
    };

    loadHeroText();

    // Set up real-time listener for updates
    const docRef = doc(db, HERO_TEXT_COLLECTION, HERO_TEXT_DOC_ID);
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setHeroText({
          titleLine1: data.titleLine1 || 'Where',
          titleHighlight: data.titleHighlight || 'TIME',
          titleLine2: data.titleLine2 || 'pays',
          description: data.description || "We don't wait for success, we accelerate it."
        });
      }
    }, (error) => {
      console.error('Error listening to hero text updates:', error);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (shouldAnimate) {
      // Small delay to ensure popup is fully closed
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [shouldAnimate]);

  // For animated gradient backgrounds, we use gradient text mode
  // This provides the best contrast and aesthetic appeal
  // The gradient text will automatically adapt to the background through CSS animations
  useEffect(() => {
    if (isVisible) {
      // Set to gradient mode for animated backgrounds
      setTextColorMode('gradient');
    }
  }, [isVisible]);

  // Split description into words for animation
  const descriptionWords = heroText.description ? heroText.description.split(' ') : [];

  return (
    <div className="hero-section" ref={heroSectionRef}>
      <div className="hero-color-accent"></div>
      <div className="hero-particles">
        <div className="particle particle-1"></div>
        <div className="particle particle-2"></div>
        <div className="particle particle-3"></div>
        <div className="particle particle-4"></div>
        <div className="particle particle-5"></div>
      </div>
      <h1 className={`hero-title hero-title-with-underline ${isVisible ? 'slide-in' : ''}`}>
        <span className={`title-word-normal word-1 ${isVisible ? 'slide-in' : ''}`}>{heroText.titleLine1}</span>{' '}
        <span className={`title-word-large word-2 ${isVisible ? 'slide-in' : ''}`}>
          <span 
            ref={timeTextRef}
            className={`word-with-underline time-text-${textColorMode}`}
          >
            {heroText.titleHighlight}
          </span>
        </span>{' '}
        <span className={`title-word-normal word-3 ${isVisible ? 'slide-in' : ''}`}>{heroText.titleLine2}</span>
      </h1>
      <p className={`hero-description ${isVisible ? 'slide-in' : ''}`}>
        {descriptionWords.map((word, index) => (
          <span
            key={`${word}-${index}`}
            className="desc-word"
            style={{ transitionDelay: `${1.5 + index * 0.15}s` }}
          >
            {word}
            {index < descriptionWords.length - 1 ? ' ' : ''}
          </span>
        ))}
      </p>
    </div>
  );
}

export default HeroText;

